/**
 * music-bed.ts — procedural ambient music bed (v2M8.7).
 *
 * Per AUDIO §5.1 the music layer has 5 beds (menu / ambient / combat
 * overlay / victory stinger / defeat stinger). v2M8.7's procedural-
 * lock ships ONE — the in-play ambient bed — to give the game audible
 * presence underneath gameplay SFX. Other beds defer to v3M11 polish
 * where commissioned ambient-electronic compositions take their place.
 *
 * Design (slow detuned sines + filter sweep, ambient-electronic
 * direction per AUDIO §1):
 *   - Three sine oscillators forming an open-fifth chord (A2 + E3 + A3,
 *     ≈110 / 165 / 220 Hz) with small per-voice detune for shimmer.
 *   - Low-pass filter with cutoff sweeping ±200 Hz around 600 Hz at
 *     0.05 Hz (~20 s cycle) — gives a slow "breath" feel without ever
 *     being a recognisable melodic figure.
 *   - Master gain LFO at 0.03 Hz (~33 s cycle) modulating volume by
 *     ±0.04 — adds organic dynamism so the bed doesn't read as a flat
 *     pad.
 *   - Total master peak ≈0.18 (≈ −15 dB on the music bus). Sits well
 *     under SFX without being inaudible.
 *
 * Lifecycle: `startAmbientBed` builds the graph + 3 s fade-in;
 * `stopAmbientBed` does a 1 s fade-out then stops the sources. Bed
 * state is held by the AudioEngine (single-instance, idempotent
 * start/stop).
 */

/**
 * Bundle of nodes the engine needs to track to gracefully tear the
 * bed down. `nodes` are all the started sources that need stop()ing;
 * `masterGain` is the chain's terminal gain for the fade ramp.
 */
export interface AmbientBedHandle {
  nodes: AudioScheduledSourceNode[];
  masterGain: GainNode;
}

/**
 * Build the ambient-bed graph wired into `target` (the music bus).
 * Sources are NOT yet `start()`-ed — the caller does that so the
 * engine can pair start with the bed-handle bookkeeping atomically.
 * The 3-second fade-in is scheduled here against `currentTime` at
 * call time, so calling `start()` immediately after gives the
 * intended ramp-up.
 */
export function buildAmbientBed(ctx: AudioContext, target: AudioNode): AmbientBedHandle {
  const now = ctx.currentTime;

  // Master bed gain — receives all voices through a low-pass filter,
  // ramps from silence to peak over 3 s.
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(0.18, now + 3.0);
  masterGain.connect(target);

  // Breath LFO — adds ±0.04 to the master gain at 0.03 Hz (~33 s).
  const breathLfo = ctx.createOscillator();
  breathLfo.type = 'sine';
  breathLfo.frequency.value = 0.03;
  const breathDepth = ctx.createGain();
  breathDepth.gain.value = 0.04;
  breathLfo.connect(breathDepth);
  breathDepth.connect(masterGain.gain);

  // Low-pass filter — voices feed in here, master gain takes the
  // filter's output. Cutoff oscillates around 600 Hz over a 20 s
  // period, giving a slow "wash" feel.
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 600;
  filter.Q.value = 1.5;
  filter.connect(masterGain);

  const filterLfo = ctx.createOscillator();
  filterLfo.type = 'sine';
  filterLfo.frequency.value = 0.05;
  const filterDepth = ctx.createGain();
  filterDepth.gain.value = 200;
  filterLfo.connect(filterDepth);
  filterDepth.connect(filter.frequency);

  // Three voice oscillators — sustained sines, slight per-voice
  // detune for chorusing. Per-voice gain divides total amplitude
  // across voices so the chord doesn't clip even at peak.
  const voiceFreqs = [110, 165, 220];
  const voiceDetunes = [0, 4, -3];
  const sources: AudioScheduledSourceNode[] = [];
  for (let i = 0; i < voiceFreqs.length; i++) {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = voiceFreqs[i];
    osc.detune.value = voiceDetunes[i];
    const voiceGain = ctx.createGain();
    voiceGain.gain.value = 1 / voiceFreqs.length;
    osc.connect(voiceGain);
    voiceGain.connect(filter);
    sources.push(osc);
  }
  sources.push(breathLfo, filterLfo);

  return { nodes: sources, masterGain };
}
